import { PythonModule } from './types';

// Keep existing modules from basicModules.ts
export { 
  PYTHON_BASICS_MODULE, 
  PYTHON_CONTROL_FLOW_MODULE, 
  PYTHON_FUNCTIONS_MODULE 
} from './basicModules';

// New Module 4: Data Structures
export const PYTHON_DATA_STRUCTURES_MODULE: PythonModule = {
  id: "python-essentials-4",
  title: "Python Data Structures",
  description: "Learn to work with Python's built-in data structures: lists, dictionaries, tuples, and sets.",
  content: `
# Python Data Structures

## Introduction to Data Structures
Data structures are ways to organize and store data efficiently. Python provides several built-in data structures that are powerful and easy to use.

## Lists
Lists are ordered collections of items that can be changed (mutable).

### Creating Lists
\`\`\`python
# Empty list
my_list = []

# List with items
fruits = ["apple", "banana", "orange"]
numbers = [1, 2, 3, 4, 5]
mixed = ["hello", 42, True, 3.14]
\`\`\`

### List Operations
\`\`\`python
# Adding items
fruits.append("grape")  # Add to end
fruits.insert(1, "kiwi")  # Insert at position

# Removing items
fruits.remove("banana")  # Remove by value
last_fruit = fruits.pop()  # Remove and return last item

# Accessing items
first_fruit = fruits[0]
last_fruit = fruits[-1]

# Slicing
some_fruits = fruits[1:3]  # Get items from index 1 to 2
\`\`\`

## Dictionaries
Dictionaries store key-value pairs and are unordered collections.

### Creating Dictionaries
\`\`\`python
# Empty dictionary
my_dict = {}

# Dictionary with data
person = {
    "name": "Alice",
    "age": 30,
    "city": "New York"
}
\`\`\`

### Dictionary Operations
\`\`\`python
# Accessing values
name = person["name"]
age = person.get("age", 0)  # Safe access with default

# Adding/updating values
person["email"] = "alice@email.com"
person["age"] = 31

# Removing items
del person["city"]
email = person.pop("email", "Not found")

# Iterating
for key, value in person.items():
    print(f"{key}: {value}")
\`\`\`

## Tuples
Tuples are ordered collections that cannot be changed (immutable).

\`\`\`python
# Creating tuples
coordinates = (10, 20)
colors = ("red", "green", "blue")

# Accessing tuple items
x = coordinates[0]
y = coordinates[1]

# Tuple unpacking
x, y = coordinates
r, g, b = colors
\`\`\`

## Sets
Sets are unordered collections of unique items.

\`\`\`python
# Creating sets
unique_numbers = {1, 2, 3, 4, 5}
letters = set("hello")  # {'h', 'e', 'l', 'o'}

# Set operations
unique_numbers.add(6)
unique_numbers.remove(1)

# Set math
set1 = {1, 2, 3}
set2 = {3, 4, 5}
union = set1 | set2  # {1, 2, 3, 4, 5}
intersection = set1 & set2  # {3}
\`\`\`

## Choosing the Right Data Structure
- **Lists**: When you need ordered, changeable collections
- **Dictionaries**: When you need key-value relationships
- **Tuples**: When you need ordered, unchangeable collections
- **Sets**: When you need unique items only
  `,
  duration: "60 minutes",
  difficulty: "beginner",
  order: 4,
  tags: ["python", "data-structures", "lists", "dictionaries", "tuples", "sets"],
  objectives: [
    "Understand Python's main data structures",
    "Create and manipulate lists effectively",
    "Work with dictionaries for key-value storage",
    "Use tuples for immutable data",
    "Apply sets for unique collections"
  ],
  pythonPractice: {
    task: "Create a contact management system using different data structures",
    initialCode: `# Contact Management System
# Create a system to store and manage contacts

# 1. Create a list to store contact names
contact_names = []

# 2. Create a dictionary to store contact details
# Format: {"name": {"phone": "123-456-7890", "email": "user@email.com"}}
contacts = {}

# 3. Function to add a new contact
def add_contact(name, phone, email):
    # Add your code here
    pass

# 4. Function to find contacts by city
def find_contacts_by_phone_area(area_code):
    # Add your code here
    pass

# 5. Test your functions
# add_contact("Alice", "555-0123", "alice@email.com")
# add_contact("Bob", "555-0456", "bob@email.com")
# print(contacts)
`,
    expectedCode: `# Contact Management System
# Create a system to store and manage contacts

# 1. Create a list to store contact names
contact_names = []

# 2. Create a dictionary to store contact details
contacts = {}

# 3. Function to add a new contact
def add_contact(name, phone, email):
    contact_names.append(name)
    contacts[name] = {
        "phone": phone,
        "email": email
    }
    print(f"Added contact: {name}")

# 4. Function to find contacts by phone area code
def find_contacts_by_phone_area(area_code):
    matching_contacts = []
    for name, details in contacts.items():
        if details["phone"].startswith(area_code):
            matching_contacts.append(name)
    return matching_contacts

# 5. Test your functions
add_contact("Alice", "555-0123", "alice@email.com")
add_contact("Bob", "555-0456", "bob@email.com")
add_contact("Charlie", "444-0789", "charlie@email.com")

print("All contacts:", contacts)
print("Names list:", contact_names)
print("555 area contacts:", find_contacts_by_phone_area("555"))
`,
    expectedOutput: {
      type: 'console',
      content: [
        "Added contact: Alice",
        "Added contact: Bob", 
        "Added contact: Charlie",
        "All contacts: {'Alice': {'phone': '555-0123', 'email': 'alice@email.com'}, 'Bob': {'phone': '555-0456', 'email': 'bob@email.com'}, 'Charlie': {'phone': '444-0789', 'email': 'charlie@email.com'}}",
        "Names list: ['Alice', 'Bob', 'Charlie']",
        "555 area contacts: ['Alice', 'Bob']"
      ]
    },
    hints: [
      "Use append() to add items to lists",
      "Store contact details as nested dictionaries",
      "Use startswith() method to check phone area codes",
      "Iterate through dictionary items with .items()"
    ]
  },
  quiz: {
    questions: [
      {
        id: "py-data-1",
        question: "Which data structure would be best for storing unique student IDs?",
        options: ["List", "Dictionary", "Tuple", "Set"],
        correctAnswer: 3
      },
      {
        id: "py-data-2",
        question: "How do you access the value associated with key 'name' in a dictionary called 'person'?",
        options: ["person.name", "person['name']", "person(name)", "person->name"],
        correctAnswer: 1
      },
      {
        id: "py-data-3",
        question: "Which data structure is immutable in Python?",
        options: ["List", "Dictionary", "Set", "Tuple"],
        correctAnswer: 3
      }
    ]
  }
};

// New Module 5: Built-in Libraries
export const PYTHON_BUILTIN_LIBRARIES_MODULE: PythonModule = {
  id: "python-essentials-5",
  title: "Python Built-in Libraries",
  description: "Explore Python's powerful built-in libraries for common programming tasks like working with dates, random numbers, and file systems.",
  content: `
# Python Built-in Libraries

## Introduction to Built-in Libraries
Python comes with a rich standard library that provides modules for many common programming tasks. These libraries save time and provide tested, efficient solutions.

## The datetime Module
Work with dates and times efficiently.

\`\`\`python
import datetime

# Current date and time
now = datetime.datetime.now()
print(f"Current time: {now}")

# Creating specific dates
birthday = datetime.date(1990, 5, 15)
meeting_time = datetime.datetime(2024, 12, 25, 14, 30)

# Formatting dates
formatted = now.strftime("%Y-%m-%d %H:%M:%S")
print(f"Formatted: {formatted}")

# Date arithmetic
tomorrow = now + datetime.timedelta(days=1)
next_week = now + datetime.timedelta(weeks=1)
\`\`\`

## The random Module
Generate random numbers and make random choices.

\`\`\`python
import random

# Random integers
dice_roll = random.randint(1, 6)
lottery_number = random.randint(1, 100)

# Random floats
probability = random.random()  # 0.0 to 1.0
price = random.uniform(10.0, 50.0)  # Between 10 and 50

# Random choices
colors = ["red", "blue", "green", "yellow"]
random_color = random.choice(colors)
random_colors = random.sample(colors, 2)  # 2 random items

# Shuffling
cards = ["A", "K", "Q", "J"]
random.shuffle(cards)
\`\`\`

## The os Module
Interact with the operating system.

\`\`\`python
import os

# Current working directory
current_dir = os.getcwd()
print(f"Current directory: {current_dir}")

# List files in directory
files = os.listdir(".")
print(f"Files: {files}")

# Check if file/directory exists
if os.path.exists("myfile.txt"):
    print("File exists!")

# Environment variables
user = os.getenv("USER", "Unknown")
path = os.getenv("PATH")
\`\`\`

## The math Module
Mathematical functions and constants.

\`\`\`python
import math

# Constants
print(f"Pi: {math.pi}")
print(f"E: {math.e}")

# Basic functions
result = math.sqrt(16)  # Square root
power = math.pow(2, 3)  # 2^3
ceiling = math.ceil(4.2)  # Round up
floor = math.floor(4.8)  # Round down

# Trigonometry
sin_val = math.sin(math.pi / 2)
cos_val = math.cos(0)
\`\`\`

## The json Module
Work with JSON data format.

\`\`\`python
import json

# Python object to JSON
person = {"name": "Alice", "age": 30, "city": "NYC"}
json_string = json.dumps(person)

# JSON to Python object
data = json.loads(json_string)
print(data["name"])

# Save to file
with open("person.json", "w") as file:
    json.dump(person, file)

# Load from file
with open("person.json", "r") as file:
    loaded_person = json.load(file)
\`\`\`

## The collections Module
Specialized container datatypes.

\`\`\`python
from collections import Counter, defaultdict

# Counter - count occurrences
text = "hello world"
letter_count = Counter(text)
print(letter_count)  # Counter({'l': 3, 'o': 2, ...})

# defaultdict - dictionary with default values
dd = defaultdict(list)
dd["fruits"].append("apple")  # Creates list automatically
\`\`\`
  `,
  duration: "50 minutes",
  difficulty: "beginner",
  order: 5,
  tags: ["python", "libraries", "datetime", "random", "os", "math", "json"],
  objectives: [
    "Use datetime module for date and time operations",
    "Generate random numbers and make random selections",
    "Interact with the file system using os module",
    "Perform mathematical calculations with math module",
    "Work with JSON data format",
    "Utilize specialized collections"
  ],
  pythonPractice: {
    task: "Build a simple game statistics tracker using multiple built-in libraries",
    initialCode: `import datetime
import random
import json

# Game Statistics Tracker
class GameStats:
    def __init__(self):
        self.games = []
    
    def play_game(self, player_name):
        # Simulate a game with random score
        # Add current timestamp and random score (1-100)
        # Store in self.games list as a dictionary
        pass
    
    def get_average_score(self, player_name):
        # Calculate average score for a specific player
        pass
    
    def get_top_score(self):
        # Return the highest score and player name
        pass
    
    def save_stats(self, filename):
        # Save game statistics to JSON file
        pass
    
    def load_stats(self, filename):
        # Load game statistics from JSON file
        pass

# Test the game tracker
# tracker = GameStats()
# tracker.play_game("Alice")
# tracker.play_game("Bob")
# tracker.play_game("Alice")
# print(f"Alice average: {tracker.get_average_score('Alice')}")
# print(f"Top score: {tracker.get_top_score()}")
`,
    expectedCode: `import datetime
import random
import json

# Game Statistics Tracker
class GameStats:
    def __init__(self):
        self.games = []
    
    def play_game(self, player_name):
        # Simulate a game with random score
        game = {
            "player": player_name,
            "score": random.randint(1, 100),
            "timestamp": datetime.datetime.now().isoformat()
        }
        self.games.append(game)
        print(f"{player_name} scored {game['score']} points!")
        return game["score"]
    
    def get_average_score(self, player_name):
        # Calculate average score for a specific player
        player_games = [game for game in self.games if game["player"] == player_name]
        if not player_games:
            return 0
        total = sum(game["score"] for game in player_games)
        return round(total / len(player_games), 2)
    
    def get_top_score(self):
        # Return the highest score and player name
        if not self.games:
            return None, 0
        top_game = max(self.games, key=lambda x: x["score"])
        return top_game["player"], top_game["score"]
    
    def save_stats(self, filename):
        # Save game statistics to JSON file
        with open(filename, "w") as file:
            json.dump(self.games, file, indent=2)
        print(f"Stats saved to {filename}")
    
    def load_stats(self, filename):
        # Load game statistics from JSON file
        try:
            with open(filename, "r") as file:
                self.games = json.load(file)
            print(f"Stats loaded from {filename}")
        except FileNotFoundError:
            print(f"File {filename} not found")
        except Exception as e:
            print(f"Error loading data: {e}")

# Test the game tracker
tracker = GameStats()
tracker.play_game("Alice")
tracker.play_game("Bob")
tracker.play_game("Alice")
print(f"Alice average: {tracker.get_average_score('Alice')}")
print(f"Top score: {tracker.get_top_score()}")
tracker.save_stats("game_stats.json")
`,
    expectedOutput: {
      type: 'console',
      content: [
        "Alice scored 87 points!",
        "Bob scored 34 points!",
        "Alice scored 92 points!",
        "Alice average: 89.5",
        "Top score: ('Alice', 92)",
        "Stats saved to game_stats.json"
      ]
    },
    hints: [
      "Use datetime.datetime.now() for timestamps",
      "Use random.randint(1, 100) for scores",
      "Filter games by player name using list comprehension",
      "Use max() with key parameter to find highest score"
    ]
  },
  quiz: {
    questions: [
      {
        id: "py-lib-1",
        question: "Which module would you use to generate a random number between 1 and 10?",
        options: ["math", "random", "datetime", "os"],
        correctAnswer: 1
      },
      {
        id: "py-lib-2",
        question: "What does json.dumps() do?",
        options: ["Loads JSON from file", "Converts Python object to JSON string", "Saves JSON to file", "Validates JSON syntax"],
        correctAnswer: 1
      },
      {
        id: "py-lib-3",
        question: "Which function from the math module would you use to round 4.7 up to the next integer?",
        options: ["math.floor()", "math.round()", "math.ceil()", "math.sqrt()"],
        correctAnswer: 2
      }
    ]
  }
};
